// The rules page (LEAGUE_SETUP.md, scenario B3's read half): the describer's
// sentences, section by section, for every member, and every version with who
// changed what, when and why. Phone: section chips over one scrolling region.
// Wider: a section rail on the left.
//
// One screen for a practice league and a real one (`data/rules.ts`); a real
// league reads `GET /settings` and `GET /settings/versions`
// (PLAN-2026-09-26-every-screen-and-crayon P1). Drawn through the kit.
//
// A RULE CHANGE UP FOR A VOTE comes first, for every member, while one is
// open (LEAGUE_SETTINGS.md "Votes", B6): what it changes, the count, Yes and
// No for a manager who may vote, the preview, and Withdraw for the
// commissioner. Counts only — nobody is shown who voted which way.

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  formatInZone,
  type LeagueRulesView,
  type RulesChange,
  type RulesProposal,
  type RulesVersion,
} from '@fsp/domain';
import { EmptyState } from '../kit';
import { Button, Caption, Line, Stack, Text, Title, tell } from '../kit/ui';
import { useReaderZone } from '../data/hooks';
import {
  useLeagueRules,
  useRulesActions,
  useRulesPreview,
  useRulesVersions,
  type VoteOutcome,
} from '../data/rules';
import { useTier } from '../shell/useTier';
import { Confirm } from './shared/Confirm';
import { FromLeague } from './shared/FromLeague';
import { PreviewTable } from './shared/RulesPreview';
import { ScoringTableView } from './shared/ScoringTableView';
import '../styles/screens/rules.css';

export function RulesScreen() {
  return (
    <FromLeague snapshot={useLeagueRules()}>{(rules) => <RulesBody rules={rules} />}</FromLeague>
  );
}

function RulesBody({ rules }: { rules: LeagueRulesView }) {
  const navigate = useNavigate();
  const phone = useTier() === 'phone';
  const region = useRef<HTMLElement>(null);
  const sections = [
    ...(rules.proposal !== null ? [{ id: 'vote', title: 'Up for a vote' }] : []),
    ...rules.document.sections.map((s) => ({ id: s.id, title: s.title })),
    { id: 'versions', title: 'Versions' },
  ];
  const inSeason = rules.league.status === 'in-season' || rules.league.status === 'playoffs';

  const jump = (id: string) => {
    const el = region.current?.querySelector<HTMLElement>(`[data-section="${id}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // `?section=versions` opens on that section: the commissioner log's rule
  // changes land on the versions they made (AUDIT_LOG.md).
  const [params, setParams] = useSearchParams();
  const asked = params.get('section');
  useEffect(() => {
    if (asked === null) return;
    const el = region.current?.querySelector<HTMLElement>(`[data-section="${asked}"]`);
    // A frame on, once the phone's sticky chips are measured (UX-38), so the
    // section lands under them rather than behind them; the query goes in the
    // same frame, since taking it away re-runs this and would cancel the jump.
    const frame = requestAnimationFrame(() => {
      el?.scrollIntoView({ block: 'start' });
      setParams({}, { replace: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [asked, setParams]);

  const nav = (
    <nav
      className={phone ? 'fsp-chip-row fsp-rules-chips' : 'fsp-rules-rail'}
      aria-label="Sections"
    >
      {sections.map((s) => (
        <button key={s.id} type="button" className="fsp-rules-jump" onClick={() => jump(s.id)}>
          {s.title}
        </button>
      ))}
    </nav>
  );

  return (
    <div className="fsp-screen fsp-flow">
      <div className="fsp-pinned">
        <Line spread gap={2}>
          <Stack gap={0}>
            <Title>League rules</Title>
            <Text tone="secondary" size="xs">
              {rules.document.headline} · {rules.league.name} · version {rules.version}
            </Text>
          </Stack>
          <Button
            size="xs"
            icon={rules.canEdit ? 'edit' : undefined}
            onClick={() => navigate(`/l/${rules.league.id}/settings`)}
          >
            {rules.canEdit ? 'Edit rules' : 'Open settings'}
          </Button>
        </Line>
      </div>
      {phone ? <div className="fsp-pinned fsp-pinned-stick">{nav}</div> : null}
      <div className="fsp-regions fsp-rules-regions">
        {phone ? null : <section className="fsp-region fsp-region-plain">{nav}</section>}
        <section className="fsp-region" aria-label="Rules" ref={region}>
          {rules.proposal !== null ? <Proposal proposal={rules.proposal} /> : null}
          {rules.document.sections.map((s) => (
            <div key={s.id} className="fsp-rules-section" data-section={s.id}>
              <h3 className="fsp-rules-title">{s.title}</h3>
              {/* THE SCORING IS A TABLE (PLAN-2026-10-18 §2.6): its name, then
                  every number by position, filtered and searched, at every
                  detail level, because a rule's number is the rule. */}
              {s.id === 'scoring' ? (
                <>
                  <p className="fsp-rules-line">{s.sentences[0]}</p>
                  <ScoringTableView table={rules.scoringTable} searchable inline />
                </>
              ) : (
                s.sentences.map((line, i) => (
                  <p key={i} className="fsp-rules-line">
                    {line}
                  </p>
                ))
              )}
              {s.id === 'schedule' ? (
                <>
                  <DivisionTeams rules={rules} />
                  <RivalTeams rules={rules} />
                </>
              ) : null}
              {s.id === 'waivers' && inSeason ? (
                <div>
                  <Button
                    size="xs"
                    variant="quiet"
                    onClick={() => navigate(`/l/${rules.league.id}/waivers`)}
                  >
                    Open waivers
                  </Button>
                </div>
              ) : null}
            </div>
          ))}
          <div className="fsp-rules-section" data-section="versions">
            <h3 className="fsp-rules-title">Versions</h3>
            <Versions />
            <Line gap={2}>
              <Text tone="secondary" size="xs">
                Everything else the commissioner did is in the log.
              </Text>
              <Button
                size="xs"
                variant="quiet"
                onClick={() => navigate(`/l/${rules.league.id}/audit`)}
              >
                Commissioner log
              </Button>
            </Line>
          </div>
        </section>
      </div>
    </div>
  );
}

/**
 * WHO IS IN EACH DIVISION, under the Schedule (PLAN-2026-10-29-divisions §5):
 * a line a division, its teams in seat order, and any team in none after them.
 */
function DivisionTeams({ rules }: { rules: LeagueRulesView }) {
  const divisions = rules.settings.schedule.divisions;
  if (divisions.length === 0 || rules.teams.length === 0) return null;
  const declared = new Set(divisions.map((d) => d.id));
  const rows = [
    ...divisions.map((d) => ({
      id: d.id,
      name: d.name,
      teams: rules.teams.filter((t) => t.divisionId === d.id),
    })),
    {
      id: '',
      name: 'No division',
      teams: rules.teams.filter((t) => t.divisionId === null || !declared.has(t.divisionId)),
    },
  ].filter((r) => r.teams.length > 0);
  return (
    <>
      {rows.map((r) => (
        <p key={r.id} className="fsp-rules-line">
          <Text weight="medium">{r.name}:</Text> {r.teams.map((t) => t.name).join(', ')}
        </p>
      ))}
    </>
  );
}

/** The pairs a rivalry week plays (PLAN-2026-10-29-the-schedule-before-the-season §4). */
function RivalTeams({ rules }: { rules: LeagueRulesView }) {
  if (rules.settings.schedule.rivalryPeriods.length === 0 || rules.teams.length === 0) return null;
  const names = new Map(rules.teams.map((t) => [t.id, t.name]));
  const seen = new Set<string>();
  const pairs: string[] = [];
  for (const t of rules.teams) {
    if (t.rivalId === null || seen.has(t.id) || !names.has(t.rivalId)) continue;
    seen.add(t.id);
    seen.add(t.rivalId);
    pairs.push(`${t.name} and ${names.get(t.rivalId)}`);
  }
  return (
    <p className="fsp-rules-line">
      <Text weight="medium">Rivals:</Text>{' '}
      {pairs.length === 0
        ? 'none yet, so a rivalry week is played as any other.'
        : `${pairs.join(' · ')}.`}
    </p>
  );
}

function Versions() {
  return (
    <FromLeague snapshot={useRulesVersions()}>
      {(view) =>
        view.versions.length === 0 ? (
          <EmptyState>No versions yet.</EmptyState>
        ) : (
          <Stack gap={3}>
            {view.versions.map((v) => (
              <VersionRow key={v.version} v={v} />
            ))}
          </Stack>
        )
      }
    </FromLeague>
  );
}

const DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

function VersionRow({ v }: { v: RulesVersion }) {
  return (
    <Stack gap={1}>
      <Text block>
        <Text weight="strong">Version {v.version}</Text>
        <Text tone="secondary">
          {' '}
          · {v.author} · {DAY.format(new Date(v.createdAt))}
          {v.scoringFrom ? ` · scoring from ${v.scoringFrom}` : ''}
        </Text>
      </Text>
      {v.reason ? <Text block>“{v.reason}”</Text> : null}
      {v.unlocked.length > 0 ? (
        <Text tone="warn" size="xs" block>
          Unlocked {v.unlocked.join(', ')} for this change.
        </Text>
      ) : null}
      <Changes changes={v.changes} />
      {v.version === 1 ? (
        <Text tone="muted" size="xs" block>
          The rules the league was made with.
        </Text>
      ) : null}
    </Stack>
  );
}

/** What a version, or a change up for a vote, changes: each section's sentences, was and now. */
function Changes({ changes }: { changes: readonly RulesChange[] }) {
  return (
    <>
      {changes.map((c) => (
        <div key={c.section} className="fsp-rules-change">
          <Caption>{c.title}</Caption>
          {c.was.map((line, i) => (
            <Text key={`was-${i}`} tone="muted" size="xs" block>
              <span className="fsp-rules-was">{line}</span>
            </Text>
          ))}
          {c.now.map((line, i) => (
            <Text key={`now-${i}`} size="xs" block>
              {line}
            </Text>
          ))}
        </div>
      ))}
    </>
  );
}

/** The rule change up for a vote (B6). */
function Proposal({ proposal: p }: { proposal: RulesProposal }) {
  const actions = useRulesActions();
  const zone = useReaderZone();
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  // The preview is the editor's own, of the change's rules: what the
  // commissioner read when they proposed it.
  const previewable = p.changes.some((c) => c.section === 'scoring' || c.section === 'roster');
  const preview = useRulesPreview(actions, p.settings, previewable && previewing);
  // No zone yet (still reading it, or it failed): say it closes, not when.
  const closes =
    zone === null
      ? null
      : formatInZone(p.closesAt, zone, { weekday: 'long', hour: 'numeric', minute: '2-digit' });
  const voted = p.votes.yes + p.votes.no;

  const act = async (run: () => Promise<VoteOutcome>, done: string) => {
    setBusy(true);
    const out = await run();
    setBusy(false);
    tell(out.ok ? done : out.message, out.ok ? 'good' : 'bad');
  };

  return (
    <div className="fsp-rules-section" data-section="vote">
      <h3 className="fsp-rules-title">Up for a vote</h3>
      <Stack gap={2}>
        <Text block>
          {p.by} proposes a change to the rules{p.reason ? ':' : '.'}
          {p.reason ? ` “${p.reason}”` : ''}
        </Text>
        <Changes changes={p.changes} />
        <Text tone="secondary" size="xs" block>
          {voted} of {p.votes.of} {p.votes.of === 1 ? 'manager has' : 'managers have'} voted:{' '}
          {p.votes.yes} yes, {p.votes.no} no. {p.votes.needed}{' '}
          {p.votes.needed === 1 ? 'yes passes' : 'yeses pass'} it.{' '}
          {p.open
            ? closes === null
              ? 'It fails if not enough have said yes before it closes.'
              : `It fails if not enough have said yes by ${closes}.`
            : 'Its time is up, and it is being counted.'}
        </Text>
        {p.canVote ? (
          <Stack gap={1}>
            <Line gap={2}>
              <Button
                size="xs"
                variant={p.myVote === 'yes' ? 'primary' : 'secondary'}
                disabled={busy}
                onClick={() => void act(() => actions.vote('yes'), 'You voted yes.')}
              >
                Yes
              </Button>
              <Button
                size="xs"
                variant={p.myVote === 'no' ? 'primary' : 'secondary'}
                disabled={busy}
                onClick={() => void act(() => actions.vote('no'), 'You voted no.')}
              >
                No
              </Button>
            </Line>
            {p.myVote !== null ? (
              <Text tone="secondary" size="xs" block>
                You said {p.myVote}. You can change it until it is decided.
              </Text>
            ) : null}
          </Stack>
        ) : null}
        <Line gap={2} wrap>
          {previewable ? (
            <Button size="xs" variant="quiet" onClick={() => setPreviewing((v) => !v)}>
              {previewing ? 'Hide the preview' : 'Preview'}
            </Button>
          ) : null}
          {p.canWithdraw ? (
            <Confirm
              label="Withdraw it"
              confirmLabel="Withdraw it?"
              busy={busy}
              onConfirm={() =>
                void act(() => actions.withdraw(), 'Withdrawn. The rules stay as they are.')
              }
            />
          ) : null}
        </Line>
        {previewable && previewing ? (
          <PreviewTable
            preview={preview.preview}
            failure={preview.failure}
            pending={preview.pending}
          />
        ) : null}
      </Stack>
    </div>
  );
}
